import type { ReactNode } from 'react';
import { useApi, useRead } from '../context';
import { useT } from '../../messages';
import type { TenantDetail as TenantDetailWire } from '../../wire';
import { ConciergeCard } from './connections/ConciergeCard';
import { KeysCard } from './connections/KeysCard';
import { UpdatesCard } from './connections/UpdatesCard';
import './connections/connections.css';

/**
 * **Connections** — what is plugged in: the AI concierge, the keys partners
 * hold, and where booking updates go and whether they got there (console
 * redesign, phase B5).
 *
 * Three cards, each leading with a verdict (plan §1, principle 3), and one
 * rule across them: **each fact appears once.** The concierge's own key and
 * update address are shown in its card, so while that card is showing them the
 * other two leave them out and say where they are. That is why the concierge's
 * read is held here rather than inside its card — the other two need to know
 * whether it is showing them.
 *
 * The concierge read is the tab's own (the partner call has no business on
 * every page load); keys and endpoints ride the tenant read. A concierge action
 * can change both — connecting makes a key and an address — so its writes
 * reload both.
 *
 * **The stage banner** (principle 6): connections belong to the second stage,
 * and until the business is taking bookings online the tab says so rather
 * than looking like a list of things it is missing. Nothing computes that
 * milestone on the server yet (see `BusinessHeader`), so it is read here as its
 * plainest evidence: the business is live, partner access is on and some
 * partner holds a working key.
 */
export function ConnectionsTab({
  tenantId,
  detail,
  onChanged,
}: {
  tenantId: string;
  detail: TenantDetailWire;
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const concierge = useRead(() => api.concierge(tenantId), [tenantId]);

  const enabledModules = detail.modules.filter((m) => m.status !== 'off').map((m) => m.module);
  const working = detail.keys.filter((k) => k.status === 'active');
  const takingOnline =
    detail.tenant.status === 'live' && enabledModules.includes('partner_api') && working.length > 0;

  const state = concierge.value;
  const conciergeOwns = state !== null && state.configured && state.concierge !== null;

  const reloadAll = (): void => {
    concierge.reload();
    onChanged();
  };

  return (
    <div className="conn">
      {takingOnline ? null : (
        <p className="conn-fence" role="note">
          {t('connections.fence')}
        </p>
      )}
      <div className="conn-grid">
        <ConciergeCard
          tenantId={tenantId}
          name={detail.tenant.name}
          read={concierge}
          conciergeKeys={working.filter((k) => k.integration === 'aiconcierge')}
          endpoint={detail.webhook_endpoints.find((e) => e.integration === 'aiconcierge') ?? null}
          onChanged={reloadAll}
        />
        <KeysCard
          tenantId={tenantId}
          keys={detail.keys}
          enabledModules={enabledModules}
          conciergeOwns={conciergeOwns}
          onChanged={onChanged}
        />
        <UpdatesCard
          tenantId={tenantId}
          endpoints={detail.webhook_endpoints}
          conciergeOwns={conciergeOwns}
          onChanged={onChanged}
        />
      </div>
    </div>
  );
}
